import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "levelButton", "hiddenInput", "weightsToggle",
    "badge", "scoreDisplay", "progressBar", "axesCount",
    "timeLabel", "submitBtn", "resetBtn", "weightBadge",
    "weightedScore"
  ]

  static values = {
    axes: Array,
    thresholds: Array,
    minScore: Number,
    maxScore: Number,
    initialScores: { type: Object, default: {} },
  }

  connect() {
    this.selections = { ...this.initialScoresValue }
    this.updateHiddenInputs()
    this.updatePanel()
  }

  select(event) {
    const button = event.currentTarget
    const axisKey = button.dataset.axisKey
    const score = parseInt(button.dataset.score, 10)

    this.selections[axisKey] = score
    this.highlightButtons(axisKey)
    this.updateHiddenInputs()
    this.updatePanel()
  }

  reset() {
    this.selections = {}
    this.levelButtonTargets.forEach((btn) => {
      btn.classList.remove("border-blue-500", "bg-blue-50", "border-2")
      btn.classList.add("border-gray-200", "border")
      const badge = btn.querySelector("[data-score-badge]")
      if (badge) {
        badge.classList.remove("bg-blue-500", "text-white")
        badge.classList.add("bg-gray-200", "text-gray-500")
      }
    })
    this.updateHiddenInputs()
    this.updatePanel()
  }

  toggleWeights() {
    this.showWeights = !this.showWeights
    this.weightsToggleTarget.textContent = this.showWeights
      ? this.weightsToggleTarget.dataset.hideLabel
      : this.weightsToggleTarget.dataset.showLabel
    this.weightBadgeTargets.forEach((el) => el.classList.toggle("hidden"))
    this.weightedScoreTargets.forEach((el) => el.classList.toggle("hidden"))
    this.levelButtonTargets.forEach((btn) => {
      const raw = btn.querySelector("[data-raw-score]")
      const weighted = btn.querySelector("[data-weighted-score]")
      if (raw && weighted) {
        raw.classList.toggle("hidden")
        weighted.classList.toggle("hidden")
      }
    })
  }

  // Private

  highlightButtons(axisKey) {
    const selected = this.selections[axisKey]
    this.levelButtonTargets
      .filter((btn) => btn.dataset.axisKey === axisKey)
      .forEach((btn) => {
        const score = parseInt(btn.dataset.score, 10)
        const isSelected = score === selected
        const badge = btn.querySelector("[data-score-badge]")

        btn.classList.toggle("border-blue-500", isSelected)
        btn.classList.toggle("bg-blue-50", isSelected)
        btn.classList.toggle("border-2", isSelected)
        btn.classList.toggle("border-gray-200", !isSelected)
        btn.classList.toggle("border", !isSelected)

        if (badge) {
          badge.classList.toggle("bg-blue-500", isSelected)
          badge.classList.toggle("text-white", isSelected)
          badge.classList.toggle("bg-gray-200", !isSelected)
          badge.classList.toggle("text-gray-500", !isSelected)
        }
      })
  }

  updateHiddenInputs() {
    const container = this.hiddenInputTarget
    container.innerHTML = ""
    for (const [key, value] of Object.entries(this.selections)) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `estimation[scores][${key}]`
      input.value = value
      input.setAttribute("form", this.element.dataset.formId)
      container.appendChild(input)
    }
  }

  updatePanel() {
    const axes = this.axesValue
    const thresholds = this.thresholdsValue
    const minScore = this.minScoreValue
    const maxScore = this.maxScoreValue
    const selectedCount = Object.keys(this.selections).length
    const axesCount = axes.length

    let totalScore = null
    if (selectedCount > 0) {
      totalScore = Object.entries(this.selections).reduce((sum, [key, score]) => {
        const axis = axes.find((a) => a.key === key)
        return axis ? sum + score * axis.weight : sum
      }, 0)
    }

    const allSelected = selectedCount === axesCount
    let sizing = null
    let pct = 0

    if (totalScore !== null && maxScore !== minScore) {
      pct = ((totalScore - minScore) / (maxScore - minScore)) * 100
      const pctRatio = pct / 100
      sizing = thresholds.find((t) => pctRatio <= t.max_percentage)
        || thresholds[thresholds.length - 1]
    }

    if (this.hasAxesCountTarget) {
      this.axesCountTarget.textContent = `${selectedCount} / ${axesCount} axes`
    }

    if (this.hasResetBtnTarget) {
      this.resetBtnTarget.classList.toggle("hidden", selectedCount === 0)
    }

    if (totalScore !== null && sizing) {
      if (this.hasBadgeTarget) {
        this.badgeTarget.textContent = sizing.label
        this.badgeTarget.style.backgroundColor = sizing.color
      }
      if (this.hasScoreDisplayTarget) {
        this.scoreDisplayTarget.textContent = `${totalScore.toFixed(1)} pts`
        this.scoreDisplayTarget.style.color = sizing.color
      }
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${pct.toFixed(1)}%`
      }
      if (this.hasTimeLabelTarget) {
        this.timeLabelTarget.textContent = sizing.time_label || ""
      }
    } else {
      if (this.hasBadgeTarget) {
        this.badgeTarget.textContent = "?"
        this.badgeTarget.style.backgroundColor = "#d1d5db"
      }
      if (this.hasScoreDisplayTarget) {
        this.scoreDisplayTarget.textContent = "—"
        this.scoreDisplayTarget.style.color = "#9ca3af"
      }
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = "0%"
      }
      if (this.hasTimeLabelTarget) {
        this.timeLabelTarget.textContent = ""
      }
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !allSelected
    }
  }
}